import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Edit2, Check, Trash2, RefreshCw, GitMerge } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { showConfirmModal, getAuthHeaders } from '../services/api';
import MergeEmployeeModal from '../components/MergeEmployeeModal';
import { readResponse } from '../utils/fetchSafe';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Group raw statuses into the 4 summary buckets shown on the page header
const STATUS_BUCKETS = {
  account_active: 'active',
  delivered: 'active',
  opened: 'active',
  clicked: 'active',
  sent: 'pending',
  queued: 'pending',
  delivery_delayed: 'pending',
  bounced: 'bounced',
  complained: 'bounced',
  failed: 'failed',
  not_sent: 'not_sent',
};

const BUCKET_META = {
  active:   { label: 'Active',   chip: 'bg-green-100 text-green-700 border-green-300 hover:bg-green-200', dot: 'bg-green-500' },
  pending:  { label: 'Pending',  chip: 'bg-blue-100 text-blue-700 border-blue-300 hover:bg-blue-200',     dot: 'bg-blue-500' },
  bounced:  { label: 'Bounced',  chip: 'bg-red-100 text-red-700 border-red-300 hover:bg-red-200',         dot: 'bg-red-500' },
  failed:   { label: 'Failed',   chip: 'bg-red-100 text-red-700 border-red-300 hover:bg-red-200',         dot: 'bg-red-500' },
  not_sent: { label: 'Not Sent', chip: 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200',     dot: 'bg-gray-400' },
};
const BUCKET_ORDER = ['active', 'pending', 'bounced', 'failed', 'not_sent'];

const bucketOf = (user) => STATUS_BUCKETS[user?.welcome_status?.status] || 'not_sent';

const UserTable = ({ users, title, showCheckmark, entriesPerPage, setEntriesPerPage, searchTerm, setSearchTerm, onEdit, onDelete, onMerge, canUpdate, canCreate, isAdmin }) => {
  const [currentPage, setCurrentPage] = useState(1);

  const filtered = users.filter(u =>
    `${u.last_name} ${u.first_name} ${u.email}`.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const totalPages = Math.ceil(filtered.length / entriesPerPage);
  const startIdx = (currentPage - 1) * entriesPerPage;
  const paged = filtered.slice(startIdx, startIdx + entriesPerPage);
  const showingFrom = filtered.length > 0 ? startIdx + 1 : 0;
  const showingTo = Math.min(startIdx + entriesPerPage, filtered.length);

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1, 2, 3, 4, 5);
      pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="mb-12">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl text-gray-700 font-light italic">{title}</h2>
        {title === 'Active Users' && canCreate && (
          <button
            onClick={() => window.location.href = '/setup/users-employees/new'}
            className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-4 py-2 rounded text-sm transition-colors"
            data-testid="new-user-btn"
          >
            + New User
          </button>
        )}
      </div>

      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          Show
          <select
            value={entriesPerPage}
            onChange={(e) => { setEntriesPerPage(Number(e.target.value)); setCurrentPage(1); }}
            className="border border-gray-300 rounded px-2 py-1"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          entries
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          Search:
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            className="border border-gray-300 rounded px-3 py-1"
          />
        </div>
      </div>

      <table className="w-full text-sm">
        <thead>
          <tr className="border-b-2">
            <th className="text-left p-3 text-gray-600 font-normal">Active</th>
            <th className="text-left p-3 text-gray-600 font-normal">Last Name</th>
            <th className="text-left p-3 text-gray-600 font-normal">First Name</th>
            <th className="text-left p-3 text-gray-600 font-normal">Email</th>
            <th className="text-left p-3 text-gray-600 font-normal">Create</th>
            <th className="text-left p-3 text-gray-600 font-normal">Update</th>
            <th className="text-left p-3 text-gray-600 font-normal">Delete</th>
            <th className="text-left p-3 text-gray-600 font-normal">Management</th>
            <th className="text-left p-3 text-gray-600 font-normal">Setup</th>
            {showCheckmark && (
              <th className="text-left p-3 text-gray-600 font-normal">Date Created</th>
            )}
            <th className="p-3"></th>
          </tr>
        </thead>
        <tbody>
          {paged.length === 0 ? (
            <tr><td colSpan={showCheckmark ? 11 : 10} className="p-4 text-center text-gray-500 italic">No users found</td></tr>
          ) : (
            paged.map((user, idx) => (
              <tr key={idx} className={`border-b ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${!showCheckmark ? 'line-through' : ''}`}>
                <td className="p-3 text-gray-700">
                  {showCheckmark && <Check className="w-4 h-4 text-gray-700" />}
                </td>
                <td className="p-3 text-gray-700">{user.last_name}</td>
                <td className="p-3 text-gray-700">{user.first_name}</td>
                <td className="p-3 text-gray-700">{user.email}</td>
                <td className="p-3 text-gray-700">{user.can_create}</td>
                <td className="p-3 text-gray-700">{user.can_update}</td>
                <td className="p-3 text-gray-700">{user.can_delete}</td>
                <td className="p-3 text-gray-700">{user.management}</td>
                <td className="p-3 text-gray-700">{user.setup}</td>
                {showCheckmark && (
                  <td className="p-3 text-gray-700 whitespace-nowrap">
                    {user.created_at
                      ? new Date(user.created_at).toLocaleDateString()
                      : <span className="text-gray-400">—</span>}
                  </td>
                )}
                <td className="p-3">
                  <div className="flex items-center gap-1">
                  {canUpdate && (
                  <button
                    onClick={() => onEdit(user._id || user.email)}
                    className="border border-gray-300 rounded p-1 hover:bg-gray-100"
                    data-testid={`edit-user-${idx}`}
                  >
                    <Edit2 className="w-4 h-4 text-gray-500" />
                  </button>
                  )}
                  {isAdmin && title === 'Inactive Users' && onMerge && (
                  <button
                    onClick={() => onMerge(user)}
                    className="border border-orange-300 rounded p-1 hover:bg-orange-50"
                    title="Merge this skeleton into an active employee"
                    data-testid={`merge-user-${idx}`}
                  >
                    <GitMerge className="w-4 h-4 text-[#E07514]" />
                  </button>
                  )}
                  {isAdmin && (
                  <button
                    onClick={() => onDelete(user._id || user.email, `${user.first_name} ${user.last_name}`)}
                    className="border border-red-300 rounded p-1 hover:bg-red-50"
                    data-testid={`delete-user-${idx}`}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                  )}
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <div className="flex justify-between items-center mt-3 text-sm text-gray-600">
        <div>Showing {showingFrom} to {showingTo} of {filtered.length} entries</div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50"
          >
            Previous
          </button>
          {getPageNumbers().map((page, i) => (
            page === '...' ? (
              <span key={i} className="px-2">...</span>
            ) : (
              <button
                key={i}
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-1 border rounded ${currentPage === page ? 'bg-[#DC3545] text-white border-[#DC3545]' : 'border-gray-300 hover:bg-gray-100'}`}
              >
                {page}
              </button>
            )
          ))}
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages || totalPages === 0}
            className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

const UsersEmployees = () => {
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();
  const isAdmin = user?.is_admin;
  const [activeUsers, setActiveUsers] = useState([]);
  const [inactiveUsers, setInactiveUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeEntries, setActiveEntries] = useState(10);
  const [inactiveEntries, setInactiveEntries] = useState(10);
  const [activeSearch, setActiveSearch] = useState('');
  const [inactiveSearch, setInactiveSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(null); // null = all
  const [refreshing, setRefreshing] = useState(false);
  const [mergeTarget, setMergeTarget] = useState(null);

  // Counts per bucket across active users only (welcome email status only applies to active accounts)
  const counts = useMemo(() => {
    const c = { active: 0, pending: 0, bounced: 0, failed: 0, not_sent: 0 };
    activeUsers.forEach(u => { c[bucketOf(u)] = (c[bucketOf(u)] || 0) + 1; });
    return c;
  }, [activeUsers]);

  const filteredActiveUsers = useMemo(() => {
    if (!statusFilter) return activeUsers;
    return activeUsers.filter(u => bucketOf(u) === statusFilter);
  }, [activeUsers, statusFilter]);

  const handleEdit = (email) => {
    navigate(`/setup/users-employees/${encodeURIComponent(email)}`);
  };

  const handleDelete = async (email, name) => {
    if (!await showConfirmModal(`Are you sure you want to delete user "${name}"?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/employees/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Failed to delete');
      toast.success(`User "${name}" deleted`);
      fetchUsers();
    } catch {
      toast.error('Failed to delete user');
    }
  };
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const [activeRes, inactiveRes] = await Promise.all([
        fetch(`${API_URL}/api/employees?active=true`),
        fetch(`${API_URL}/api/employees?active=false`)
      ]);
      const { data: activeData } = await readResponse(activeRes);
      const { data: inactiveData } = await readResponse(inactiveRes);
      setActiveUsers(activeData.employees || []);
      setInactiveUsers(inactiveData.employees || []);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshStatuses = async () => {
    setRefreshing(true);
    try {
      const res = await fetch(`${API_URL}/api/employees/refresh-welcome-statuses`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const { data } = await readResponse(res);
      if (!res.ok) {
        toast.error(data.detail || 'Failed to refresh statuses');
        return;
      }
      toast.success(`Refreshed ${data.updated || 0} of ${data.polled || 0} email statuses${data.errors ? ` (${data.errors} errors)` : ''}`);
      await fetchUsers();
    } catch (err) {
      toast.error(`Failed to refresh statuses: ${err.message}`);
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-8 text-center text-gray-500">Loading users...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 lg:p-8">
        <div className="mb-6">
          <h2 className="text-lg text-[#5B8DB8] font-light">Setup</h2>
          <h1 className="text-3xl text-gray-700 font-light italic">Users</h1>
        </div>

        <hr className="mb-6" />

        <div className="mb-6">
          <button
            onClick={() => navigate('/setup')}
            className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded transition-colors"
            data-testid="back-btn"
          >
            Back
          </button>
        </div>

        {/* Hints */}
        <div className="bg-white rounded shadow p-6 mb-8 space-y-3">
          <div className="flex items-start gap-2">
            <span className="bg-[#FFA94D] text-white text-xs font-bold px-2 py-1 rounded flex-shrink-0">Hint</span>
            <p className="text-gray-600 text-sm">Staff members requiring access to application shall have a user profile entered in setup requiring a unique user email & password. Production personnel not requiring system access shall be entered for job costing & time tracking purposes. Users created not requiring application access shall have email & password fields left blank.</p>
          </div>
          <div className="flex items-start gap-2">
            <span className="bg-[#FFA94D] text-white text-xs font-bold px-2 py-1 rounded flex-shrink-0">Hint</span>
            <p className="text-gray-600 text-sm">Pay rate is required for Production Personnel at current hourly pay rate.</p>
          </div>
          <div className="flex items-start gap-2">
            <span className="bg-[#FFA94D] text-white text-xs font-bold px-2 py-1 rounded flex-shrink-0">Hint</span>
            <p className="text-gray-600 text-sm">"Inactive" box is to be checked only when a user is no longer in the organization. User email & password fields should also be blank.</p>
          </div>
          <div className="flex items-start gap-2">
            <span className="bg-[#FFA94D] text-white text-xs font-bold px-2 py-1 rounded flex-shrink-0">Hint</span>
            <p className="text-gray-600 text-sm">Select appropriate User: Roles/Certifications/Franchise Access/Application Access</p>
          </div>
        </div>

        {/* Welcome Email Status Summary */}
        <div className="bg-white rounded shadow p-4 mb-6" data-testid="welcome-email-summary">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-gray-600 uppercase tracking-wide font-medium mr-2">Welcome Email Status</span>
              <button
                onClick={() => setStatusFilter(null)}
                className={`px-3 py-1 rounded-full border text-sm font-medium transition-colors ${
                  statusFilter === null
                    ? 'bg-gray-700 text-white border-gray-700'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                }`}
                data-testid="filter-chip-all"
              >
                All ({activeUsers.length})
              </button>
              {BUCKET_ORDER.map(bucket => {
                const meta = BUCKET_META[bucket];
                const count = counts[bucket] || 0;
                const active = statusFilter === bucket;
                return (
                  <button
                    key={bucket}
                    onClick={() => setStatusFilter(active ? null : bucket)}
                    className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-sm font-medium transition-colors ${
                      active ? 'ring-2 ring-offset-1 ring-gray-400 ' : ''
                    }${meta.chip}`}
                    data-testid={`filter-chip-${bucket}`}
                  >
                    <span className={`w-2 h-2 rounded-full ${meta.dot}`}></span>
                    {count} {meta.label}
                  </button>
                );
              })}
            </div>
            <button
              onClick={handleRefreshStatuses}
              disabled={refreshing}
              className="inline-flex items-center gap-2 bg-[#4ECFDB] hover:bg-[#3DB8C4] disabled:opacity-60 text-white px-4 py-2 rounded text-sm transition-colors"
              data-testid="refresh-statuses-btn"
              title="Poll Resend for the latest delivery status of every pending welcome email"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing…' : 'Refresh statuses'}
            </button>
          </div>
          {statusFilter && (
            <div className="mt-3 text-xs text-gray-500 italic">
              Filtering active users by <strong>{BUCKET_META[statusFilter].label}</strong>. Click the chip again or "All" to clear.
            </div>
          )}
        </div>

        {/* Active Users */}
        <UserTable
          users={filteredActiveUsers}
          title="Active Users"
          showCheckmark={true}
          entriesPerPage={activeEntries}
          setEntriesPerPage={setActiveEntries}
          searchTerm={activeSearch}
          setSearchTerm={setActiveSearch}
          onEdit={handleEdit}
          onDelete={handleDelete}
          canUpdate={hasPermission('can_update')}
          canCreate={hasPermission('can_create')}
          isAdmin={isAdmin}
        />

        {/* Inactive Users */}
        <UserTable
          users={inactiveUsers}
          title="Inactive Users"
          showCheckmark={false}
          entriesPerPage={inactiveEntries}
          setEntriesPerPage={setInactiveEntries}
          searchTerm={inactiveSearch}
          setSearchTerm={setInactiveSearch}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onMerge={isAdmin ? setMergeTarget : null}
          canUpdate={hasPermission('can_update')}
          canCreate={hasPermission('can_create')}
          isAdmin={isAdmin}
        />
      </div>

      {mergeTarget && (
        <MergeEmployeeModal
          skeleton={mergeTarget}
          activeUsers={activeUsers}
          onClose={() => setMergeTarget(null)}
          onMerged={() => { setMergeTarget(null); fetchUsers(); }}
        />
      )}
    </Layout>
  );
};

export default UsersEmployees;
